import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarRange, Loader2, Trash2, Download, TrendingUp,
  BarChart3, Info,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import { shortDateWithYear } from '../../utils/maturityChart';
import type {
  GrowingRegion, CultivarRegionMaturity, RegionOffset,
} from '../../types/cultivar';

// When this cultivar was first picked, in which growing region, in which
// season — the record the maturity charts and region offsets read from, and
// what the orchard module's harvest-window suggestion is derived from.
//
// This panel is DERIVED, not entered. Maturity is an evaluation reading: it is
// captured as a `maturity_date` observation on an orchard visit, alongside
// every other thing measured that day, and rolled up here per region and
// season. There was once a second entry form on this panel, which meant the
// same fact could be typed twice in two shapes — and a hand-typed row wins
// permanently, since `deriveObservedRecords` skips any region/season already
// marked `manual`. A stale figure that silently outranks every observation
// taken since is worse than no figure.

interface Props {
  cultivarId: number;
  cultivarName: string;
  canEdit: boolean;
}

export function MaturityByRegionPanel({ cultivarId, cultivarName, canEdit }: Props) {
  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [records, setRecords] = useState<CultivarRegionMaturity[]>([]);
  const [offsets, setOffsets] = useState<RegionOffset[]>([]);
  const [loading, setLoading] = useState(true);
  const [deriving, setDeriving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, rec, off] = await Promise.all([
        cultivarService.listGrowingRegions(),
        cultivarService.listCultivarMaturity(cultivarId),
        cultivarService.getRegionOffsets(),
      ]);
      if (reg.success && reg.data) setRegions(reg.data);
      if (rec.success && rec.data) setRecords(rec.data);
      if (off.success && off.data) setOffsets(off.data);
      setError(null);
    } catch (e: unknown) {
      setError(errorText(e, 'Failed to load maturity data'));
    } finally {
      setLoading(false);
    }
  }, [cultivarId]);

  useEffect(() => { load(); }, [load]);

  const regionsById = useMemo(
    () => new Map(regions.map((r) => [r.id, r])),
    [regions]
  );

  // Group by season so the most recent work is at the top and each season's
  // regional spread reads as a block.
  const bySeason = useMemo(() => {
    const map = new Map<number, CultivarRegionMaturity[]>();
    for (const r of records) {
      if (!map.has(r.season_year)) map.set(r.season_year, []);
      map.get(r.season_year)!.push(r);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, rows]) => [year, rows.sort((a, b) => a.first_pick_date < b.first_pick_date ? -1 : 1)] as const);
  }, [records]);

  // Only offsets touching a region this cultivar actually has data in are
  // relevant here; the rest belong on a global analytics view.
  const relevantOffsets = useMemo(() => {
    const mine = new Set(records.map((r) => r.growing_region_id));
    return offsets.filter((o) => mine.has(o.region_a_id) || mine.has(o.region_b_id));
  }, [offsets, records]);

  const handleDerive = async () => {
    setDeriving(true);
    setError(null);
    try {
      const res = await cultivarService.deriveMaturityFromObservations(cultivarId);
      const count = (res as { derived_count?: number }).derived_count ?? 0;
      if (count === 0) {
        setError(
          'No maturity observations could be attributed to a region. An evaluation ' +
          'needs a growing region set on it, or on the farm it names.'
        );
      }
      await load();
    } catch (e: unknown) {
      setError(errorText(e, 'Failed to derive'));
    } finally {
      setDeriving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this maturity record?')) return;
    try {
      await cultivarService.deleteMaturityRecord(id);
      setRecords((prev) => prev.filter((r) => r.id !== id));
    } catch (e: unknown) {
      window.alert(errorText(e, 'Failed'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5" />Maturity by region
            </CardTitle>
            {canEdit && (
              <Button variant="outline" onClick={handleDerive} disabled={deriving}>
                {deriving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                <span className="hidden sm:inline">Rebuild from observations</span>
                <span className="sm:hidden">Rebuild</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {error && <div className="mb-3 p-2 rounded bg-error-50 text-error-800 text-sm">{error}</div>}

          {regions.length === 0 && !loading && (
            <div className="mb-4 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
              <p>
                No growing regions exist yet. A region is a climate area such as “Goulburn Valley”
                — not a state or licensing territory, since places within one state can harvest
                weeks apart.
              </p>
              {canEdit && (
                <p className="mt-1.5">
                  Create one while recording an evaluation, or manage them all in{' '}
                  <Link to="/cultivar/admin/growing-regions" className="underline font-medium hover:text-warning-900">
                    Cultivar Admin
                  </Link>.
                </p>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-8">
              <CalendarRange className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
              <p className="text-sm text-neutral-500">No maturity records for {cultivarName} yet.</p>
              <p className="text-xs text-neutral-400 mt-1 max-w-md mx-auto">
                Record a “Maturity date” reading on an evaluation, with a growing region set,
                then rebuild from observations.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {bySeason.map(([year, rows]) => (
                <div key={year}>
                  <h4 className="text-sm font-semibold text-neutral-700 mb-2">Season {year}</h4>
                  <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                    {rows.map((r) => (
                      <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-neutral-900">{r.region_name}</span>
                            {r.hemisphere && (
                              <span className="text-[10px] uppercase bg-neutral-100 text-neutral-600 rounded px-1.5 py-0.5">
                                {r.hemisphere}
                              </span>
                            )}
                            <span
                              className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${
                                r.source === 'manual'
                                  ? 'bg-primary-100 text-primary-700'
                                  : 'bg-success-100 text-success-700'
                              }`}
                              // A manual row is no longer creatable, but older ones
                              // survive — and they block re-derivation for their
                              // region and season, so say so rather than leaving a
                              // stale figure looking like a current one.
                              title={r.source === 'manual'
                                ? 'Typed in by hand. Rebuilding from observations skips this region and season until it is deleted.'
                                : `Averaged from ${r.observation_count} maturity date observation(s)`}
                            >
                              {r.source === 'manual' ? 'Entered by hand' : `From ${r.observation_count} obs`}
                            </span>
                          </div>
                          <div className="text-xs text-neutral-600 mt-0.5">
                            First pick <span className="font-medium">{shortDateWithYear(r.first_pick_date)}</span>
                            {r.last_pick_date && <> · last pick {shortDateWithYear(r.last_pick_date)}</>}
                            {r.harvest_window_days != null && <> · {r.harvest_window_days} day window</>}
                          </div>
                          {r.notes && <p className="text-xs text-neutral-500 mt-0.5">{r.notes}</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Opens the master chart narrowed to this record's
                              region and season, with this cultivar picked out.
                              That view used to be a modal of its own; it is the
                              same chart with two filters set, so it is a link
                              rather than a second implementation. */}
                          <Link
                            to={`/cultivar/maturity-chart?regions=${r.growing_region_id}&seasons=${r.season_year}&cultivar_id=${cultivarId}`}
                            className="text-neutral-400 hover:text-primary-600 p-1"
                            title="Show the maturity chart for this region and season"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Link>
                          {canEdit && (
                            <button
                              onClick={() => handleDelete(r.id)}
                              className="text-neutral-400 hover:text-error-600 p-1"
                              title="Delete this maturity record"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Region offsets — the derived "region A runs N days ahead of region B". */}
      {relevantOffsets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />Region timing differences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-neutral-500 mb-3 flex items-start gap-1">
              <Info className="h-3.5 w-3.5 mt-px shrink-0" />
              Derived from cultivars picked in both regions in the same season, so a region
              that happens to grow only late cultivars doesn't read as a late region.
              Cross-hemisphere pairs are excluded.
            </p>
            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
              {relevantOffsets.map((o) => {
                const a = regionsById.get(o.region_a_id);
                const b = regionsById.get(o.region_b_id);
                const later = o.median_offset_days >= 0 ? b : a;
                const earlier = o.median_offset_days >= 0 ? a : b;
                return (
                  <div key={`${o.region_a_id}-${o.region_b_id}`} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-neutral-800">
                        <span className="font-medium">{later?.name ?? `Region ${o.region_b_id}`}</span>
                        {' picks '}
                        <span className="font-medium">{Math.abs(o.median_offset_days)} days</span>
                        {' after '}
                        <span className="font-medium">{earlier?.name ?? `Region ${o.region_a_id}`}</span>
                      </span>
                      <span className="text-xs text-neutral-500">
                        {o.sample_size} pairs · ±{o.stddev_days}d · confidence {Math.round(o.confidence * 100)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}

function errorText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return err?.response?.data?.error?.message || err?.message || fallback;
}

export default MaturityByRegionPanel;
